import type {
  EffectiveChannelInput,
  EffectiveDigestInput,
  EffectiveDigestSettings,
  EffectiveMobilePushWhenAwayInput,
  EffectiveSoundInput,
  EffectiveSoundUrlInput,
  NotificationScenarioKey,
  ScenarioOverrides,
  UpdateUserPreferencesInput,
  UserNotificationSettingsValue,
} from "@/types/notification";

export function effectiveChannelEnabled({
  schema,
  values,
  scenario,
  channel,
}: EffectiveChannelInput): boolean {
  const row =
    scenario === undefined
      ? undefined
      : schema.scenarios.find((s) => s.key === scenario);

  // No row means nothing to mute: the server decided this alert carries no
  // preference, so the client has nothing to filter on - the same rule the
  // backend applies to a definition with no scenario. An ineligible channel on
  // a row that DOES exist is still off.
  if (!row) return true;
  if (!row.channels.includes(channel)) return false;

  return (
    values.scenarios?.[row.key]?.[channel] ?? row.defaults[channel] ?? false
  );
}

export function effectiveSound({
  schema,
  values,
}: EffectiveSoundInput): boolean {
  return values.sound ?? schema.soundDefault;
}

export function effectiveMobilePushWhenAway({
  schema,
  values,
}: EffectiveMobilePushWhenAwayInput): boolean {
  return values.mobilePushWhenAway ?? schema.mobilePushWhenAwayDefault;
}

/**
 * Which file the user's pick resolves to. The alert's own URL is only a
 * fallback: every alert that makes a sound makes the user's sound, so a
 * per-type chime cannot contradict the one they chose in settings.
 */
export function effectiveSoundUrl({
  schema,
  values,
  fallbackUrl,
}: EffectiveSoundUrlInput): string {
  const key = values.soundKey ?? schema.soundKeyDefault;

  return schema.sounds.find((sound) => sound.key === key)?.url ?? fallbackUrl;
}

export function effectiveDigest({
  schema,
  values,
  type,
}: EffectiveDigestInput): EffectiveDigestSettings | undefined {
  const declared = schema.digest.types.find((k) => k.key === type);

  if (!declared) return undefined;

  return values.digest?.[type] ?? { ...declared.defaults, enabled: false };
}

export function mergePreferenceValues(
  values: UserNotificationSettingsValue,
  input: UpdateUserPreferencesInput,
): UserNotificationSettingsValue {
  const scenarios: Partial<Record<NotificationScenarioKey, ScenarioOverrides>> =
    { ...values.scenarios };

  for (const [key, overrides] of Object.entries(input.scenarios ?? {})) {
    const scenario = key as NotificationScenarioKey;

    scenarios[scenario] = { ...scenarios[scenario], ...overrides };
  }

  return {
    sound: input.sound ?? values.sound,
    soundKey: input.soundKey ?? values.soundKey,
    mobilePushWhenAway: input.mobilePushWhenAway ?? values.mobilePushWhenAway,
    scenarios,
    // A patched type replaces its stored record wholesale, matching the server's
    // merge arm; a type the patch does not mention survives.
    digest: input.digest
      ? { ...values.digest, ...input.digest }
      : values.digest,
  };
}
